import {
  Component,
  DoCheck,
  Input,
  IterableDiffer,
  IterableDiffers,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { OrderableIngredient, OrderedIngredientService, StoreConfig } from 'ngx-web-api';
import { ViewportService } from '../../../../core/services/viewport.service';
import { ViewPort } from '../../../../domain/view-port.model';
import { distinctUntilChanged } from 'rxjs/operators';
import { BreakpointService } from '../../../../core/services/breakpoint.service';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons/faQuestionCircle';

@Component({
  selector: 'fts-ingredient-info',
  templateUrl: './ingredient-info.component.html',
  styleUrls: ['./ingredient-info.component.scss'],
})
export class IngredientInfoComponent implements OnInit, OnChanges, DoCheck {
  @Input()
  ingredient: OrderableIngredient;
  @Input()
  isUnavailable: boolean;
  @Input()
  showIngredientPrices: boolean;
  @Input()
  itemSize: string;
  @Input()
  isHalf: boolean;
  @Input()
  orderedQualifiers: string[];
  @Input()
  orderedSecondQualifiers: string[];
  @Input()
  ingredientIndex: number;
  @Input()
  isInModal: boolean;
  @Input()
  ingredientPrice: number;
  @Input()
  storeConfig: StoreConfig;
  @Input()
  isRadioButton = false;
  @Input()
  inItemEditorModal: boolean;
  @Input()
  isHalfAndHalf = false;
  @Input()
  isHalfSelected = false;

  @Output()
  onCaloriesCalc: EventEmitter<number> = new EventEmitter();
  @Output()
  onServingsCalc: EventEmitter<string> = new EventEmitter();
  @Output()
  onDisplayedIngredientPriceCalc: EventEmitter<number> = new EventEmitter();
  @Output()
  onAvailableSizesCalc: EventEmitter<string> = new EventEmitter();

  @ViewChild('descriptionBtn', { static: false })
  descriptionBtn;

  calories: number;
  servingLabel?: string;
  isAbove1200pixelsWidth = false;
  isPopOverLeft = false;
  faQuestionCircle = faQuestionCircle;
  displayedIngredientPrice: number;

  private differ: IterableDiffer<string>;
  availableSizes: string;

  constructor(
    private orderedIngredientService: OrderedIngredientService,
    private _iterableDiffers: IterableDiffers,
    private viewportService: ViewportService,
    public breakPointService: BreakpointService
  ) {
    this.differ = this._iterableDiffers.find([]).create(null);
  }

  ngOnInit() {
    this.viewportService.viewport$.pipe(distinctUntilChanged()).subscribe((viewport: ViewPort) => {
      this.isAbove1200pixelsWidth = viewport.isAbove1200pixels;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.calcCalories();

    if (changes['ingredient'] || changes['itemSize'] || changes['ingredientPrice']) {
      this.calculateIngredientPrice();
    }
  }

  ngDoCheck(): void {
    // We do this since OnChanges is called when input instance changes, which in our case does not apply since
    // we simply append/remove from the qualifiers array
    const changes = this.differ.diff(this.orderedQualifiers);
    if (changes) {
      this.calcCalories();
    }
  }

  calculateIngredientPrice(): void {
    this.displayedIngredientPrice = this.ingredientPrice || this.getIngredientSizePrice();
    if (this.showIngredientPrices && this.displayedIngredientPrice) {
      this.onDisplayedIngredientPriceCalc.emit(this.displayedIngredientPrice);
    } else {
      this.onDisplayedIngredientPriceCalc.emit(0);
    }
  }

  ingredientDescriptionClicked(event) {
    event.preventDefault();
    if (event.stopPropagation) {
      event.stopPropagation();
    } else {
      event.cancelBubble = true;
    }
    this.computePopoverPlacement();
  }

  computePopoverPlacement() {
    if (
      (!this.isInModal && this.isAbove1200pixelsWidth && (this.ingredientIndex + 1) % 3 === 0) ||
      (!this.isAbove1200pixelsWidth && this.ingredientIndex % 2 !== 0) ||
      (this.isInModal && this.isAbove1200pixelsWidth && this.ingredientIndex % 2 !== 0)
    ) {
      this.isPopOverLeft = true;
    } else {
      this.isPopOverLeft = false;
    }
  }

  private calcCalories(): void {
    this.calories = this.orderedIngredientService.getCalories(
      this.ingredient,
      this.itemSize,
      this.isHalf,
      this.orderedQualifiers,
      this.orderedSecondQualifiers
    );
    if (this.ingredient.determinesRecipe) {
      const selectedSize = this.ingredient.sizePrices.find(s => s.size === this.itemSize);
      this.servingLabel = selectedSize ? selectedSize.servingsLabel : undefined;
    }
    this.onCaloriesCalc.emit(this.calories);
    this.onServingsCalc.emit(this.servingLabel);
  }

  private getIngredientSizePrice(): number {
    if (!this.ingredient || !this.itemSize) {
      return undefined;
    }
    const sizePrice = this.ingredient.getSizePrice(this.itemSize);
    if (!sizePrice && this.storeConfig.showSizeSpecificIngredients) {
      this.availableSizes = '';
      this.availableSizes = this.ingredient.sizePrices.map(sp => sp.size).join(', ');
      this.onAvailableSizesCalc.emit(this.availableSizes);
    }
    if (this.isHalfAndHalf && !this.storeConfig.halfPricePolicyAsWhole && !this.isHalfSelected) {
      return !!sizePrice && sizePrice.halfPrice;
    }
    return !!sizePrice && sizePrice.price;
  }
}
